import { resolveField } from '../models';

/** A field name (dotted paths work) or a function. */
export type ShOptionAccessor<T, R> = string | ((option: T) => R);

/** What `sh-select` and `sh-multiselect` hand to PrimeNG: `raw` is the caller's option. */
export interface ShOptionItem<T> {
  label: string;
  value: unknown;
  raw: T;
}

/**
 * Maps the caller's options to PrimeNG's option shape. One place defines how
 * `optionLabel` / `optionValue` (a field name or a function) are read, so
 * select and multi-select agree. Without `optionValue` the option itself is
 * the value; without `optionLabel` the option's `label` (or the option, for a
 * primitive) is the text.
 */
export function buildOptionItems<T>(
  options: readonly T[] | null | undefined,
  optionLabel: ShOptionAccessor<T, string> | undefined,
  optionValue: ShOptionAccessor<T, unknown> | undefined
): ShOptionItem<T>[] {
  return (options ?? []).map((raw) => {
    const label =
      typeof optionLabel === 'function'
        ? optionLabel(raw)
        : resolveField(raw, optionLabel ?? 'label');
    const value =
      typeof optionValue === 'function'
        ? optionValue(raw)
        : optionValue
        ? resolveField(raw, optionValue)
        : raw;
    return {
      label: label === undefined || label === null ? '' : String(label),
      value,
      raw,
    };
  });
}
